import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMExportOutput,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import {ReactNode} from 'react';

import styles from './EnhancedHeadingNode.module.css';
import {HeadingSettingsButton} from './HeadingSettingsButton';

export type SerializedHeadingControlsNode = Spread<
  {
    headingId: string;
    showSettings: boolean;
  },
  SerializedLexicalNode
>;

export class HeadingControlsDecoratorNode extends DecoratorNode<ReactNode> {
  __headingId: string;
  __showSettings: boolean;

  static getType(): string {
    return 'HeadingControls';
  }

  static clone(node: HeadingControlsDecoratorNode): HeadingControlsDecoratorNode {
    return new HeadingControlsDecoratorNode(node.__headingId, node.__showSettings, node.__key);
  }

  constructor(headingId: string, showSettings: boolean = true, key?: NodeKey) {
    super(key);
    this.__headingId = headingId;
    this.__showSettings = showSettings;
  }

  createDOM(): HTMLElement {
    const element = document.createElement('div');
    element.setAttribute('data-lexical-heading-controls', 'true');
    element.classList.add(styles.controls);
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    // Return null to exclude from copy/paste operations
    return {element: null};
  }

  static importDOM(): null {
    // Don't import heading control nodes from external content
    return null;
  }

  static importJSON(serializedNode: SerializedHeadingControlsNode): HeadingControlsDecoratorNode {
    const {headingId, showSettings} = serializedNode;
    return $createHeadingControlsDecoratorNode(headingId, showSettings);
  }

  exportJSON(): SerializedHeadingControlsNode {
    return {
      headingId: this.__headingId,
      showSettings: this.__showSettings,
      type: 'HeadingControls',
      version: 1,
    };
  }

  getTextContent(): string {
    // Return empty string so it doesn't affect text content
    return '';
  }

  isKeyboardSelectable(): boolean {
    // Make this node non-navigable with keyboard
    return false;
  }

  isIsolated(): boolean {
    // Make this node behave as a single, indivisible unit
    // This prevents the cursor from entering the node during navigation
    return true;
  }

  // isInline(): boolean {
  //   // Make this node inline so it doesn't create line breaks
  //   return true;
  // }

  // canBeEmpty(): boolean {
  //   // Allow this node to be empty
  //   return true;
  // }

  // canInsertTextBefore(): boolean {
  //   // Prevent text insertion before this node
  //   return false;
  // }

  // canInsertTextAfter(): boolean {
  //   // Prevent text insertion after this node
  //   return false;
  // }

  getHeadingId(): string {
    return this.__headingId;
  }

  getShowSettings(): boolean {
    return this.__showSettings;
  }

  setShowSettings(showSettings: boolean): void {
    const writable = this.getWritable();
    writable.__showSettings = showSettings;
  }

  component(): ReactNode {
    return null;
  }

  decorate(): ReactNode {
    return this.__showSettings && <HeadingSettingsButton headingId={this.__headingId} />;
  }
}

export function $createHeadingControlsDecoratorNode(
  headingId: string,
  showSettings: boolean = true
): HeadingControlsDecoratorNode {
  return $applyNodeReplacement(new HeadingControlsDecoratorNode(headingId, showSettings));
}

export function $isHeadingControlsDecoratorNode(
  node: unknown
): node is HeadingControlsDecoratorNode {
  return node instanceof HeadingControlsDecoratorNode;
}
